import { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  Add,
  Edit,
  Delete,
  Lightbulb,
  Settings,
} from '@mui/icons-material'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import { TableRow, TableCell, TableHeader } from '@tiptap/extension-table'
import { BorderlessTable } from '../extensions/BorderlessTable'
import { ResizableImage, ImageBubbleMenu } from '../extensions/ResizableImage'
import { TipTapToolbar } from './TipTapToolbar'
import { useAdventureEditorStore } from '../../../stores/adventureEditorStore'
import {
  useAdventureClues,
  useClueSettings,
  useCreateClue,
  useUpdateClue,
  useDeleteClue,
  useUpdateClueSettings,
} from '../../../hooks/useAdventureClues'
import type { AdventureClueDto } from '@api/types/clues'

// ====================
// CLUE EDITOR DIALOG
// ====================

interface ClueEditorDialogProps {
  open: boolean
  onClose: () => void
  onSave: (title: string, htmlContent: string) => void
  initialTitle?: string
  initialContent?: string
  isSaving?: boolean
}

function ClueEditorDialog({
  open,
  onClose,
  onSave,
  initialTitle = '',
  initialContent = '',
  isSaving = false,
}: ClueEditorDialogProps) {
  const [title, setTitle] = useState(initialTitle)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      BorderlessTable.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      ResizableImage.configure({ inline: false }),
    ],
    content: initialContent,
  }, [open, initialContent])

  const handleSave = () => {
    if (!title.trim() || !editor) return
    onSave(title.trim(), editor.getHTML())
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialTitle ? 'Edit Clue' : 'Add Clue'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Clue Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
        />
        <Typography variant="subtitle2" gutterBottom>
          Content
        </Typography>
        <Box className="rich-editor-container" sx={{ overflow: 'visible', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {editor && <TipTapToolbar editor={editor} />}
          <EditorContent editor={editor} className="tiptap-editor" />
          {editor && <ImageBubbleMenu editor={editor} />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!title.trim() || isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ====================
// MAIN COMPONENT
// ====================

export function CluesPanel() {
  const adventureId = useAdventureEditorStore((s) => s.adventureId)

  // Queries
  const { data: clues, isLoading: cluesLoading } = useAdventureClues(adventureId)
  const { data: settings, isLoading: settingsLoading } = useClueSettings(adventureId)

  // Mutations
  const createClue = useCreateClue(adventureId)
  const updateClue = useUpdateClue(adventureId)
  const deleteClue = useDeleteClue(adventureId)
  const updateSettings = useUpdateClueSettings(adventureId)

  // Dialog state
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingClue, setEditingClue] = useState<AdventureClueDto | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // Local settings state
  const [penaltySeconds, setPenaltySeconds] = useState<number>(settings?.cluePenaltySeconds ?? 0)
  const [maxClues, setMaxClues] = useState<number>(settings?.maxCluesAllowed ?? 0)
  const [carryPenalty, setCarryPenalty] = useState<boolean>(settings?.carryCluesPenaltyOnRetry ?? false)

  // Sync local state when settings load from server (not during user edits)
  useEffect(() => {
    if (settings && !updateSettings.isPending) {
      setPenaltySeconds(settings.cluePenaltySeconds)
      setMaxClues(settings.maxCluesAllowed)
      setCarryPenalty(settings.carryCluesPenaltyOnRetry)
    }
  }, [settings?.cluePenaltySeconds, settings?.maxCluesAllowed, settings?.carryCluesPenaltyOnRetry])

  const handleAddClue = useCallback(() => {
    setEditingClue(null)
    setEditorOpen(true)
  }, [])

  const handleEditClue = useCallback((clue: AdventureClueDto) => {
    setEditingClue(clue)
    setEditorOpen(true)
  }, [])

  const handleSaveClue = useCallback(async (title: string, htmlContent: string) => {
    if (editingClue) {
      await updateClue.mutateAsync({ clueId: editingClue.id, data: { title, htmlContent } })
    } else {
      await createClue.mutateAsync({ title, htmlContent })
    }
    setEditorOpen(false)
    setEditingClue(null)
  }, [editingClue, updateClue, createClue])

  const handleDeleteClue = useCallback(async () => {
    if (deleteConfirmId) {
      await deleteClue.mutateAsync(deleteConfirmId)
      setDeleteConfirmId(null)
    }
  }, [deleteConfirmId, deleteClue])

  const handleToggleActive = useCallback(async (clue: AdventureClueDto) => {
    await updateClue.mutateAsync({
      clueId: clue.id,
      data: { isActive: !clue.isActive },
    })
  }, [updateClue])

  const handleSaveSettings = useCallback(() => {
    updateSettings.mutate({
      cluePenaltySeconds: penaltySeconds,
      maxCluesAllowed: maxClues,
      carryCluesPenaltyOnRetry: carryPenalty,
    })
  }, [penaltySeconds, maxClues, carryPenalty, updateSettings])

  if (cluesLoading || settingsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  const sortedClues = [...(clues || [])].sort((a, b) => a.orderIndex - b.orderIndex)

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Lightbulb color="primary" />
        <Typography variant="h6">Clues</Typography>
        <Chip label={`${sortedClues.length} clues`} size="small" />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define clues at the adventure level. Use Clue Gate nodes in the workflow to unlock clues into a player's session pool. Players can then request clues on demand.
      </Typography>

      {/* Clue List */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        {sortedClues.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No clues defined yet</Typography>
          </Box>
        ) : (
          <List dense>
            {sortedClues.map((clue, index) => (
              <Box key={clue.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {clue.title}
                        </Typography>
                        {!clue.isActive && (
                          <Chip label="Inactive" size="small" color="default" />
                        )}
                      </Box>
                    }
                    secondary={`Order: ${clue.orderIndex}`}
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      size="small"
                      checked={clue.isActive}
                      onChange={() => handleToggleActive(clue)}
                    />
                    <IconButton size="small" onClick={() => handleEditClue(clue)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setDeleteConfirmId(clue.id)} color="error">
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Paper>

      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={handleAddClue}
        sx={{ mb: 4 }}
      >
        Add Clue
      </Button>

      {/* Settings Section */}
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Settings color="action" />
        <Typography variant="h6">Clue Settings</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          size="small"
          type="number"
          label="Penalty per Clue (seconds)"
          value={penaltySeconds}
          onChange={(e) => setPenaltySeconds(parseInt(e.target.value, 10) || 0)}
          inputProps={{ min: 0, max: 3600, step: 1 }}
          helperText={
            penaltySeconds > 0
              ? `${Math.floor(penaltySeconds / 60)}m ${penaltySeconds % 60}s added per clue used`
              : 'No penalty for using clues'
          }
        />

        <TextField
          size="small"
          type="number"
          label="Max Clues Allowed"
          value={maxClues}
          onChange={(e) => setMaxClues(parseInt(e.target.value, 10) || 0)}
          inputProps={{ min: 0, max: 100, step: 1 }}
          helperText={maxClues === 0 ? 'Unlimited clues' : `Players can use up to ${maxClues} clues`}
        />

        <FormControlLabel
          control={
            <Switch
              checked={carryPenalty}
              onChange={(e) => setCarryPenalty(e.target.checked)}
            />
          }
          label="Carry clue penalty on retry"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5, ml: 5.5 }}>
          If enabled, time penalties from clues persist when a player retries the adventure.
        </Typography>

        <Button
          variant="contained"
          size="small"
          onClick={handleSaveSettings}
          disabled={updateSettings.isPending}
          sx={{ alignSelf: 'flex-start', mt: 1 }}
        >
          {updateSettings.isPending ? 'Saving...' : 'Save Settings'}
        </Button>

        {updateSettings.isSuccess && (
          <Alert severity="success" sx={{ mt: 1 }}>Settings saved.</Alert>
        )}
      </Box>

      {/* Add/Edit Dialog */}
      {editorOpen && (
        <ClueEditorDialog
          open={editorOpen}
          onClose={() => { setEditorOpen(false); setEditingClue(null) }}
          onSave={handleSaveClue}
          initialTitle={editingClue?.title}
          initialContent={editingClue?.htmlContent}
          isSaving={createClue.isPending || updateClue.isPending}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Clue?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently remove this clue. Any Clue Gate nodes referencing it will need to be updated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button onClick={handleDeleteClue} color="error" disabled={deleteClue.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
